import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Home, GraduationCap, ShoppingBag, Building2, Stethoscope, Car, TreePine, Shield, ChevronDown, Calculator, Clock, CheckCircle2 } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { phases } from '../data/phases';
import { products } from '../data/products';
import { calculateInstallment, formatPrice } from '../utils/calculator';

const PAGE_URL = 'https://rumahparkserpong.id/park-serpong-tangerang-selatan';
const WA_NUMBER = '628892273880';

const buildWaLink = (text) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;

const phaseSummaries = {
  'phase-1': {
    tagline: 'Entry-level homes mulai Rp 299 Juta',
    handover: 'December 2024',
    cover: '/images/products/phase1/z1.png',
  },
  'phase-2': {
    tagline: 'Modern Zen Living, akses Cendana Suites',
    handover: 'Q1 2025',
    cover: '/images/products/phase2/q10.png',
  },
  'phase-3': {
    tagline: 'Premium 9en Collection — koleksi paling lengkap',
    handover: 'Q2 2025',
    cover: '/images/products/phase3/z5.png',
  },
  'phase-4': {
    tagline: 'Funtastic Four — pilihan keluarga muda',
    handover: 'Q3 2025',
    cover: '/images/products/phase4/z6.png',
  },
  'phase-5': {
    tagline: 'Hi5 Collection — TreeTops & GoldTops 3 lantai',
    handover: 'Q4 2025',
    cover: '/images/products/phase5/treetops-t33.png',
  },
  'phase-6': {
    tagline: 'Enamaste — koleksi termewah Park Serpong',
    handover: 'Q1 2026',
    cover: '/images/products/phase6/treetops-alpha-t33.png',
  },
  'commercials': {
    tagline: 'The Hive @ Parkhills — ruko investasi',
    handover: '2026',
    cover: '/images/products/commercial/hive-a-a.png',
  },
};

const facilities = [
  { icon: GraduationCap, title: 'Lentera School', desc: 'Sekolah berkualitas internasional di dalam kawasan', color: 'bg-blue-100 text-blue-700' },
  { icon: ShoppingBag, title: 'Mall & Retail', desc: 'Pusat perbelanjaan modern + Supermall Karawaci 5 menit', color: 'bg-pink-100 text-pink-700' },
  { icon: Building2, title: 'University', desc: 'Akses dekat Universitas Pelita Harapan & SGU', color: 'bg-purple-100 text-purple-700' },
  { icon: Stethoscope, title: 'Rumah Sakit', desc: 'RS Siloam Lippo Village & faskes lengkap', color: 'bg-red-100 text-red-700' },
  { icon: TreePine, title: 'Taman & Jogging Track', desc: 'Area hijau + club house + swimming pool', color: 'bg-green-100 text-green-700' },
  { icon: Shield, title: 'Security 24/7', desc: 'CCTV, one-gate system, patrol berkala', color: 'bg-orange-100 text-orange-700' },
];

const locationAdvantages = [
  { time: '5 menit', place: 'Gading Serpong', icon: Car },
  { time: '5 menit', place: 'Lippo Karawaci & Supermall Karawaci', icon: Car },
  { time: '10 menit', place: 'Tol Jakarta-Merak (Exit Karawaci)', icon: Car },
  { time: '15 menit', place: 'BSD City & AEON Mall', icon: Car },
  { time: '15 menit', place: 'Stasiun Commuter Line Cicayur', icon: Car },
  { time: '30 menit', place: 'Bandara Soekarno-Hatta', icon: Car },
];

const faqs = [
  {
    q: 'Berapa harga rumah di Park Serpong Tangerang Selatan?',
    a: 'Park Serpong menawarkan rumah mulai dari Rp 299 Juta (XYZ Livin Z1) hingga Rp 2,27 Miliar (Blackstar Corner). Total ada 60+ tipe yang tersebar di 7 fase pengembangan, sehingga Anda bisa memilih sesuai budget dan kebutuhan keluarga.',
  },
  {
    q: 'Di mana lokasi tepat Park Serpong?',
    a: 'Park Serpong berada di Jl. Raya Legok, Tangerang, Banten — masuk dalam segitiga emas Karawaci - Gading Serpong - BSD. Hanya 5 menit ke Lippo Karawaci, 10 menit ke Tol Jakarta-Merak, dan 15 menit ke BSD City.',
  },
  {
    q: 'Apa saja fasilitas publik di Park Serpong?',
    a: 'Park Serpong adalah kawasan kota mandiri 400 hektar dengan fasilitas lengkap: Lentera School, Mall, akses Universitas (UPH/SGU), Rumah Sakit Siloam, club house, swimming pool, jogging track, dan security 24/7.',
  },
  {
    q: 'Bagaimana skema cicilan KPR Park Serpong?',
    a: 'Cicilan fleksibel dengan tenor 5, 10, 15, hingga 20 tahun. DP mulai 2.5%, bekerja sama dengan bank-bank besar. Cicilan bulanan bisa dimulai dari Rp 1,9 Juta per bulan untuk tipe paling terjangkau (XYZ Livin Z1).',
  },
  {
    q: 'Siapa developer Park Serpong?',
    a: 'Park Serpong dikembangkan oleh Lippo Karawaci / LippoLand — developer properti terpercaya dengan pengalaman 40+ tahun di Indonesia, juga pengembang Lippo Village, St. Moritz, dan Holland Village.',
  },
  {
    q: 'Kapan handover unit Park Serpong?',
    a: 'Jadwal handover bertahap mengikuti fase: Phase 1 sudah handover Desember 2024, Phase 2 Q1 2025, Phase 3 Q2 2025, Phase 4 Q3 2025, Phase 5 Q4 2025, Phase 6 Q1 2026, dan Commercials di 2026.',
  },
];

export const PanduanLengkapPage = () => {
  const [tenor, setTenor] = useState(15);
  const [selectedProductId, setSelectedProductId] = useState('p1-z1');
  const [openFaq, setOpenFaq] = useState(0);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) || products[0],
    [selectedProductId]
  );

  const calc = useMemo(
    () => calculateInstallment(selectedProduct.price, tenor),
    [selectedProduct, tenor]
  );

  // SEO meta
  useEffect(() => {
    document.title = 'Park Serpong Tangerang Selatan - Panduan Lengkap Rumah, Cicilan, Lokasi & Fasilitas 2026';

    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    setMeta(
      'description',
      'Panduan lengkap Park Serpong Tangerang Selatan: 60+ tipe rumah mulai Rp 299 Juta, simulasi KPR, fasilitas Lentera School, Mall, Universitas, RS, dan keunggulan lokasi 5 menit ke Karawaci/Gading Serpong.'
    );
    setMeta(
      'keywords',
      'park serpong tangerang selatan, rumah park serpong, harga park serpong, lokasi park serpong, fasilitas park serpong, kpr park serpong, lippo karawaci serpong, rumah murah tangerang'
    );
    setMeta('og:title', 'Park Serpong - Panduan Lengkap Rumah, Cicilan & Lokasi', 'property');
    setMeta(
      'og:description',
      'Semua info Park Serpong di satu halaman: tipe rumah, simulasi KPR, fasilitas, dan keunggulan lokasi.',
      'property'
    );
    setMeta('og:url', PAGE_URL, 'property');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    // FAQ Schema for rich snippet
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    let faqScript = document.getElementById('faq-schema');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.id = 'faq-schema';
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const script = document.getElementById('faq-schema');
      if (script) script.remove();
    };
  }, []);

  // Group products by phase for display
  const phaseGroups = useMemo(() => {
    return phases.map((phase) => {
      const phaseProducts = products.filter((p) => p.phase_id === phase.id);
      const minPrice = Math.min(...phaseProducts.map((p) => p.price));
      const maxPrice = Math.max(...phaseProducts.map((p) => p.price));
      return {
        ...phase,
        ...phaseSummaries[phase.id],
        productCount: phaseProducts.length,
        minPrice,
        maxPrice,
        products: phaseProducts,
      };
    });
  }, []);

  const featuredProducts = useMemo(
    () => [
      products.find((p) => p.id === 'p1-z1'),
      products.find((p) => p.id === 'p2-q10'),
      products.find((p) => p.id === 'p3-q12'),
      products.find((p) => p.id === 'p4-q15'),
      products.find((p) => p.id === 'p5-treetops-t33'),
      products.find((p) => p.id === 'p6-treetops-alpha-33'),
    ].filter(Boolean),
    []
  );

  return (
    <div className="min-h-screen bg-white" data-testid="panduan-lengkap-page">
      <Navbar scrolled={true} currentPage="" />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900" data-testid="panduan-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/images/beranda/park_serpong.jpeg"
            alt="Park Serpong Tangerang Selatan"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative container mx-auto px-4 max-w-5xl text-center text-white">
          <span className="inline-block bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold mb-4" data-testid="hero-badge">
            PANDUAN LENGKAP 2026
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="hero-title">
            Park Serpong Tangerang Selatan
          </h1>
          <p className="text-lg md:text-2xl mb-3 text-blue-100" data-testid="hero-subtitle">
            Semua yang perlu Anda tahu — tipe rumah, cicilan, fasilitas & lokasi
          </p>
          <p className="text-base md:text-lg mb-8 text-blue-200 max-w-3xl mx-auto" data-testid="hero-description">
            Kawasan kota mandiri 400 hektar di segitiga emas Karawaci - Gading - BSD. 60+ pilihan rumah mulai <strong className="text-yellow-300">Rp 299 Juta</strong> dari developer terpercaya Lippo Karawaci.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={buildWaLink('Halo! Saya tertarik dengan Park Serpong dan ingin info lengkap. Terima kasih!')}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 px-8 py-4 rounded-xl font-bold inline-flex items-center gap-2 shadow-2xl hover:scale-105 transition-all"
              data-testid="hero-wa-btn"
            >
              <Phone size={20} /> Konsultasi Gratis via WhatsApp
            </a>
            <a
              href="#tipe-rumah"
              className="bg-white/10 hover:bg-white/20 backdrop-blur border-2 border-white/30 px-8 py-4 rounded-xl font-bold inline-flex items-center gap-2 transition-all"
              data-testid="hero-explore-btn"
            >
              Lihat Tipe Rumah <ChevronDown size={20} />
            </a>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto" data-testid="hero-stats">
            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/20">
              <p className="text-2xl md:text-3xl font-bold text-yellow-300">400 Ha</p>
              <p className="text-xs md:text-sm text-blue-100">Luas Kawasan</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/20">
              <p className="text-2xl md:text-3xl font-bold text-yellow-300">60+</p>
              <p className="text-xs md:text-sm text-blue-100">Tipe Rumah</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/20">
              <p className="text-2xl md:text-3xl font-bold text-yellow-300">7</p>
              <p className="text-xs md:text-sm text-blue-100">Fase Pengembangan</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/20">
              <p className="text-2xl md:text-3xl font-bold text-yellow-300">299 Jt</p>
              <p className="text-xs md:text-sm text-blue-100">Mulai Dari</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tentang Singkat */}
      <section className="py-16 bg-white" data-testid="about-section">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-testid="about-title">
            Tentang Park Serpong
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-lg leading-relaxed mb-4">
              <strong>Park Serpong</strong> adalah kawasan kota mandiri seluas <strong>400 hektar</strong> yang dikembangkan oleh <strong>Lippo Karawaci (LippoLand)</strong>, developer properti dengan reputasi 40+ tahun di Indonesia. Berlokasi strategis di <strong>Jl. Raya Legok, Tangerang, Banten</strong>, kawasan ini berada tepat di tengah segitiga emas <em>Karawaci - Gading Serpong - BSD City</em>.
            </p>
            <p className="text-lg leading-relaxed">
              Mengusung tagline <strong>#LebihMasukAkal</strong>, Park Serpong menyajikan kombinasi sempurna: hunian residensial multi-tipe, area komersial (The Hive @ Parkhills), fasilitas publik lengkap (sekolah, mall, universitas, rumah sakit), dan infrastruktur modern dalam satu kawasan terintegrasi. Dengan 60+ tipe rumah mulai dari <strong>Rp 299 Juta hingga Rp 2,27 Miliar</strong>, ada pilihan untuk semua kebutuhan keluarga.
            </p>
          </div>
        </div>
      </section>

      {/* Tipe Rumah - 7 Phases */}
      <section id="tipe-rumah" className="py-16 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="phases-section">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="phases-title">
              Tipe Rumah Park Serpong — 7 Fase Pengembangan
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Dari rumah entry-level hingga premium, semua tipe siap menjadi pilihan investasi atau hunian Anda.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {phaseGroups.map((phase) => (
              <Link
                key={phase.id}
                to="/produk"
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden transition-all hover:-translate-y-1"
                data-testid={`phase-card-${phase.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={phase.cover}
                    alt={`${phase.name} - ${phase.description}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {phase.productCount} TIPE
                  </div>
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur text-xs font-semibold px-3 py-1 rounded-full text-gray-700 flex items-center gap-1">
                    <Clock size={12} /> {phase.handover}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{phase.tagline}</p>
                  <div className="flex items-baseline gap-2 pt-3 border-t">
                    <span className="text-xs text-gray-500">Mulai</span>
                    <span className="text-2xl font-bold text-blue-600">Rp {formatPrice(phase.minPrice)}</span>
                    <span className="text-xs text-gray-400">- {formatPrice(phase.maxPrice)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/produk"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold transition-all hover:scale-105 shadow-lg"
              data-testid="phases-cta-btn"
            >
              Jelajahi Semua 60+ Tipe Rumah <Home size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Simulasi Cicilan */}
      <section className="py-16 bg-white" data-testid="calculator-section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <Calculator className="mx-auto text-blue-600 mb-3" size={48} />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="calculator-title">
              Simulasi Cicilan KPR Park Serpong
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hitung perkiraan cicilan bulanan Anda dengan DP 2.5% dan tenor fleksibel hingga 20 tahun.
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-2xl p-6 md:p-10 border-2 border-blue-100" data-testid="calculator-card">
            {/* Pilih Tipe */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3">Pilih Tipe Rumah Favorit</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {featuredProducts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedProductId(p.id)}
                    className={`p-3 rounded-xl text-left text-sm font-semibold transition-all border-2 ${
                      selectedProductId === p.id
                        ? 'bg-blue-600 text-white border-blue-700 shadow-lg scale-105'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                    }`}
                    data-testid={`calc-product-${p.id}`}
                  >
                    <div className="font-bold truncate">{p.name}</div>
                    <div className={`text-xs ${selectedProductId === p.id ? 'text-blue-100' : 'text-gray-500'}`}>
                      Rp {formatPrice(p.price)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tenor */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3">Jangka Waktu Cicilan</label>
              <div className="grid grid-cols-4 gap-3">
                {[5, 10, 15, 20].map((y) => (
                  <button
                    key={y}
                    onClick={() => setTenor(y)}
                    className={`py-3 rounded-xl font-bold transition-all ${
                      tenor === y
                        ? 'bg-blue-600 text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 hover:bg-blue-50 shadow'
                    }`}
                    data-testid={`calc-tenor-${y}`}
                  >
                    {y} Thn
                  </button>
                ))}
              </div>
            </div>

            {/* Result */}
            <div className="bg-white rounded-2xl p-6 shadow-inner border-2 border-green-200" data-testid="calc-result">
              <p className="text-sm text-gray-600 mb-1">Cicilan per bulan untuk <strong>{selectedProduct.name}</strong></p>
              <p className="text-4xl md:text-5xl font-bold text-green-600 mb-4">
                Rp {formatPrice(calc.monthly_payment)} <span className="text-lg text-gray-500">/bulan</span>
              </p>
              <div className="grid grid-cols-3 gap-3 text-sm pt-4 border-t">
                <div>
                  <p className="text-gray-500">DP (2.5%)</p>
                  <p className="font-bold text-gray-900">Rp {formatPrice(calc.down_payment)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Pinjaman</p>
                  <p className="font-bold text-gray-900">Rp {formatPrice(calc.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Bayar</p>
                  <p className="font-bold text-gray-900">Rp {formatPrice(calc.total_payment)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 italic">*Estimasi dengan bunga 3.8% — syarat & ketentuan berlaku</p>
            </div>

            <a
              href={buildWaLink(`Halo! Saya ingin info cicilan ${selectedProduct.name} dengan tenor ${tenor} tahun. Terima kasih!`)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
              data-testid="calc-wa-btn"
            >
              <Phone size={20} /> Tanya Detail KPR via WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Fasilitas Publik */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="facilities-section">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="facilities-title">
              Fasilitas Publik Park Serpong
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Konsep kota mandiri menghadirkan semua kebutuhan keluarga dalam jarak walking distance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  data-testid={`facility-card-${idx}`}
                >
                  <div className={`inline-flex p-3 rounded-xl ${f.color} mb-4`}>
                    <Icon size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Keunggulan Lokasi */}
      <section className="py-16 bg-white" data-testid="location-section">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <MapPin className="mx-auto text-red-500 mb-3" size={48} />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="location-title">
              Keunggulan Lokasi Park Serpong
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Berada di pusat segitiga emas Karawaci - Gading Serpong - BSD City, dengan akses ke berbagai destinasi penting.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {locationAdvantages.map((loc, idx) => {
              const Icon = loc.icon;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100"
                  data-testid={`location-item-${idx}`}
                >
                  <div className="bg-blue-600 text-white rounded-lg p-3 flex-shrink-0">
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 truncate">{loc.place}</p>
                    <p className="text-sm text-blue-600 font-semibold">{loc.time}</p>
                  </div>
                  <CheckCircle2 size={20} className="text-green-500 flex-shrink-0" />
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl" data-testid="location-cta">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Kenapa Park Serpong Pilihan Tepat?</h3>
            <div className="grid md:grid-cols-3 gap-6 mt-8 text-left">
              <div>
                <CheckCircle2 className="text-yellow-300 mb-2" size={32} />
                <h4 className="font-bold text-lg mb-1">Lokasi Premium</h4>
                <p className="text-blue-100 text-sm">Akses tol, kereta, dan pusat bisnis dalam radius 15 menit</p>
              </div>
              <div>
                <CheckCircle2 className="text-yellow-300 mb-2" size={32} />
                <h4 className="font-bold text-lg mb-1">Developer Terpercaya</h4>
                <p className="text-blue-100 text-sm">Lippo Karawaci dengan track record 40+ tahun</p>
              </div>
              <div>
                <CheckCircle2 className="text-yellow-300 mb-2" size={32} />
                <h4 className="font-bold text-lg mb-1">Investasi Aman</h4>
                <p className="text-blue-100 text-sm">Capital gain tinggi di kawasan growing kedua di Indonesia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="faq-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10" data-testid="faq-title">
            Pertanyaan Umum Park Serpong
          </h2>
          <div className="space-y-3">
            {faqs.map((f, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-md overflow-hidden"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  className="w-full text-left px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-bold text-gray-900 pr-4">{f.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-blue-600 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 text-gray-700 leading-relaxed border-t pt-4" data-testid={`faq-answer-${idx}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white" data-testid="final-cta-section">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-testid="final-cta-title">
            Siap Memiliki Rumah di Park Serpong?
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Tim kami siap membantu Anda memilih tipe yang tepat, simulasi KPR, hingga jadwalkan kunjungan show unit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={buildWaLink('Halo! Saya tertarik dengan Park Serpong, mohon info lengkap dan jadwalkan kunjungan. Terima kasih!')}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 px-10 py-5 rounded-xl font-bold inline-flex items-center justify-center gap-2 shadow-2xl hover:scale-105 transition-all text-lg"
              data-testid="final-wa-btn"
            >
              <Phone size={24} /> WhatsApp Sekarang
            </a>
            <Link
              to="/produk"
              className="bg-white/10 hover:bg-white/20 backdrop-blur border-2 border-white/30 px-10 py-5 rounded-xl font-bold inline-flex items-center justify-center gap-2 transition-all text-lg"
              data-testid="final-produk-btn"
            >
              Lihat Katalog Lengkap
            </Link>
          </div>
          <p className="text-blue-200 text-sm mt-8" data-testid="final-contact">
            📞 08892273880  •  ✉ info@parkserpong.com  •  📍 Jl. Raya Legok, Tangerang, Banten
          </p>
        </div>
      </section>

      {/* WhatsApp Floating */}
      <a
        href={buildWaLink('Halo! Saya ingin bertanya mengenai rumah di Park Serpong lebih lanjut. Terima Kasih')}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 z-40"
        data-testid="whatsapp-floating-widget"
        aria-label="Hubungi via WhatsApp"
      >
        <svg className="w-8 h-8 md:w-10 md:h-10" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute inset-0 rounded-full bg-green-400 opacity-75 animate-ping"></span>
      </a>
    </div>
  );
};

export default PanduanLengkapPage;
